import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchResumeData } from '../../../fetchProjects';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const Education = () => {
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllEdu, setShowAllEdu] = useState(false);
  const [showAllCert, setShowAllCert] = useState(false);
  const eduRef = useRef(null);
  const certRef = useRef(null);

  useEffect(() => {
    const fetchEducation = async () => {
      try {
        const data = await fetchResumeData();
        const filteredEdu = data.filter(item => item.type === 'education');
        const filteredCert = data.filter(item => item.type === 'certification');
        setEducation(filteredEdu);
        setCertifications(filteredCert);
      } catch (err) {
        console.error('Failed to fetch education:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEducation();
  }, []);

  if (!loading && education.length === 0 && certifications.length === 0) return null;

  const visibleEdu = showAllEdu ? education : education.slice(0, 4);
  const visibleCert = showAllCert ? certifications : certifications.slice(0, 6);

  const handleToggleEdu = () => {
    if (showAllEdu && eduRef.current) {
      const yOffset = -100;
      const y = eduRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setShowAllEdu(!showAllEdu);
  };

  const handleToggleCert = () => {
    if (showAllCert && certRef.current) {
      const yOffset = -100;
      const y = certRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setShowAllCert(!showAllCert);
  };

  return (
    <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-30"></div>
      
      <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-6 relative z-10">
        Education & Certifications
      </h2>

      <div className="flex flex-col gap-10">
        {/* Education */}
        <div ref={eduRef} className="relative z-10">
          <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
            Academic Background
          </h3>

              {loading ? (
                <div className="flex items-center justify-center py-12 relative z-10 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-accent"></div>
                </div>
              ) : education.length === 0 ? (
                <p className="text-text-muted text-sm relative z-10">No education entries found.</p>
              ) : (
                visibleEdu.map((edu) => (
                  <div key={edu.id} className="bg-white/5 border border-white/10 rounded-lg p-5 mb-6 group hover:border-accent/30 transition-smooth">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-3">
                      <div className="flex items-center gap-4">
                        {edu.badge && edu.badge.startsWith('http') ? (
                          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center p-1.5 overflow-hidden shadow-sm border border-white/10 flex-shrink-0">
                            <Image src={edu.badge} alt={`${edu.organization} logo`} className="w-full h-full object-contain" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icon name="GraduationCap" size={20} className="text-text-muted" />
                          </div>
                        )}
                        <div>
                          <h4 className="font-heading font-bold text-lg text-text-primary">
                            {edu.title}
                          </h4>
                          <p className="text-accent font-semibold">{edu.organization}</p>
                          <p className="text-text-secondary text-sm">{edu.location} {edu.duration ? `• ${edu.duration}` : ''}</p>
                        </div>
                      </div>
                    </div>

                    {(edu.slug || edu.description) && (
                      <div className="mb-4">
                        {edu.slug && edu.slug.split('___')[0].trim().length > 0 && edu.slug.split('___')[0].length < 100 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {edu.slug.split('___')[0].split(',').map((badge, idx) => (
                              <span key={idx} className="bg-success/10 border border-success/20 text-success-light px-3 py-1 rounded-full text-sm font-medium">
                                {badge.trim()}
                              </span>
                            ))}
                          </div>
                        )}
                        {edu.description && (
                          <p className="text-text-secondary text-sm italic border-l-2 border-warning/50 pl-3 py-1">
                            {edu.description}
                          </p>
                        )}
                      </div>
                    )}

                    {edu.technologies && edu.technologies.length > 0 && (
                      <div className="mb-4">
                        <h5 className="font-semibold text-text-primary mb-2">Relevant Coursework:</h5>
                        <div className="flex flex-wrap gap-2">
                          {edu.technologies.map((course, index) => (
                            <span key={index} className="bg-white/5 px-2 py-1 rounded text-xs text-text-secondary border border-white/5">
                              {course}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {edu.achievements && edu.achievements.length > 0 && (
                      <div>
                        <h5 className="font-semibold text-text-primary mb-2">Activities & Achievements:</h5>
                        <ul className="space-y-1">
                          {edu.achievements.map((activity, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0 shadow-[0_0_6px_rgba(14,165,233,0.3)]"></div>
                              <span className="text-text-secondary text-sm">{activity}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {edu.detailedContent && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <Link 
                          to={`/resume/${edu.id}`} 
                          className="inline-flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent-light transition-smooth group"
                          aria-label={`Read deeper details regarding my academic background at ${edu.organization}`}
                          title={`View ${edu.organization} Details`}
                        >
                          Read More
                          <Icon name="ArrowRight" size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    )}
                  </div>
                ))
              )}

              {education.length > 4 && (
                <div className="mt-2 flex justify-center">
                  <button
                    onClick={handleToggleEdu}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-6 py-2 text-accent hover:text-accent-light text-sm font-semibold transition-all duration-300 relative z-10"
                    aria-label={showAllEdu ? 'Show fewer academic entries' : 'Show all academic entries'}
                    title="Toggle Education List"
                  >
                    {showAllEdu ? 'Show Less' : `Read More (${education.length - 4} More)`}
                  </button>
                </div>
              )}
        </div>

        {/* Certifications */}
        <div ref={certRef} className="relative z-10">
          <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
            Professional Certifications
          </h3>

          <div className="space-y-4">
            {certifications.length === 0 ? (
              <p className="text-text-muted text-sm relative z-10">No certifications found.</p>
            ) : (
              visibleCert.map((cert) => (
                <div key={cert.id} className="relative group block">
                {/* Wrap the entire card in a Link to the Detail Page if detailedContent exists */}
                {cert.detailedContent ? (
                  <Link to={`/resume/${cert.id}`} className="absolute inset-0 z-10 rounded-lg" aria-label={`Read deeper specifics about the ${cert.title || cert.name} certification`} title={`View Certification: ${cert.title || cert.name}`}></Link>
                ) : null}
                
                <div className={`relative z-20 pointer-events-none bg-white/5 border border-white/10 rounded-lg p-5 transition-all duration-300 flex flex-col sm:flex-row items-start gap-4 sm:gap-6 ${cert.detailedContent ? 'group-hover:border-accent/50 group-hover:bg-white/10 group-hover:shadow-[0_4px_20px_rgba(14,165,233,0.15)] group-hover:-translate-y-1' : ''}`}>
                  {/* Fixed Logo Column for Perfect Alignment */}
                  <div className="w-12 flex-shrink-0 flex justify-center">
                    {cert.badge && cert.badge.startsWith('http') ? (
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center p-1.5 overflow-hidden shadow-sm border border-white/10 flex-shrink-0">
                         <Image src={cert.badge} alt={`${cert.organization} logo`} className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                         <Icon name="Award" size={20} className="text-text-muted" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 w-full flex flex-col justify-center pt-0.5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4">
                      <h4 className={`font-semibold text-lg truncate leading-tight ${cert.detailedContent ? 'group-hover:text-accent transition-colors' : 'text-text-primary'}`}>
                        {cert.title || cert.name}
                      </h4>
                      <span className="bg-white/5 border border-white/10 text-text-secondary px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap hidden sm:inline-block ml-auto">
                        {cert.date_value || cert.date}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2">
                        {cert.slug && cert.slug.split('___')[0].trim().length > 0 && cert.slug.split('___')[0].length < 100 && (
                          cert.slug.split('___')[0].split(',').map((badge, idx) => (
                            <span key={idx} className="text-[10px] font-bold tracking-wider uppercase text-text-secondary bg-white/5 border border-white/10 px-2 py-0.5 rounded leading-none flex items-center h-5">
                              {badge.trim()}
                            </span>
                          ))
                        )}
                        
                        <span className="text-accent font-medium text-sm flex items-center h-5">
                           {cert.organization || cert.issuer}
                        </span>

                        {cert.duration && cert.duration.startsWith('http') && (
                          <a 
                            href={cert.duration} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="relative z-30 pointer-events-auto inline-flex items-center gap-1 text-xs font-semibold text-text-primary hover:text-accent transition-smooth border border-white/10 hover:border-accent/30 px-2 rounded bg-white/5 hover:bg-white/10 h-5"
                            aria-label={`View the official credential verification for ${cert.title || cert.name}`}
                            title="Verify Certification Object"  
                          >
                            <Icon name="ExternalLink" size={12} />
                            Show Credential
                          </a>
                        )}

                        {cert.detailedContent && (
                          <span className="text-xs font-semibold text-accent flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ml-auto h-5">
                             Read More <Icon name="ArrowRight" size={12} className="group-hover:translate-x-1 transition-transform" />
                          </span>
                        )}
                    </div>
                  </div>
                </div>
                </div>
              ))
            )}
          </div>

          {certifications.length > 6 && (
            <div className="mt-6 flex justify-center">
              <button 
                onClick={handleToggleCert}
                className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-6 py-2 text-accent hover:text-accent-light text-sm font-semibold transition-all duration-300 relative z-10"
                aria-label={showAllCert ? 'Show fewer certification items' : 'Show all certification items'}
                title="Toggle Certifications List"
              >
                {showAllCert ? 'Show Less' : `Read More (${certifications.length - 6} More)`}
              </button>
            </div>
          )}

          <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-transparent opacity-20"></div>
            <h4 className="font-semibold text-text-primary mb-2 relative z-10">Continuous Learning</h4>
            <p className="text-text-secondary text-sm relative z-10">
              Regularly advancing my skills in space research, remote sensing, and AI for geoscience. 
              Actively participating in government-led and international scientific programs.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;
